import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useSupportIssues,
  useRaisedIssues,
  useIssue,
  useCreateIssue,
  useUpdateIssueStatus,
  useEscalateIssue,
  useAddIssueComment
} from '../hooks/useSupport';
import {
  LifeBuoy,
  Send,
  Clock,
  CheckCircle2,
  ShieldAlert,
  ArrowUpRight,
  MessageSquare,
  ChevronRight,
  User,
  Building2,
  CornerUpLeft,
  SendHorizontal
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { Issue, IssueCategory, IssueSeverity, IssueStatus } from '../types';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const PLATFORM_ROOT_ID = 'platform-root';

export const SupportPage = () => {
  const { node, getTerm } = useTenant();
  const { user } = useCurrentUser();
  const { can, isAdmin: isGlobalAdmin } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<IssueStatus | 'ALL'>('ALL');
  const [queueTab, setQueueTab] = useState<'QUEUE' | 'RAISED'>('QUEUE');
  const [isCreating, setIsCreating] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [reply, setReply] = useState('');
  const [isConfirmingEscalate, setIsConfirmingEscalate] = useState(false);

  // Deep link support: /support?ticket=<id> is how the global support portal opens a ticket.
  const requestedTicketId = searchParams.get('ticket');

  const supportTerm = getTerm('support');
  const parentNodeTerm = getTerm('parentNode');

  const [newIssue, setNewIssue] = useState({
    title: '',
    description: '',
    category: IssueCategory.TECHNICAL,
    severity: IssueSeverity.MEDIUM,
  });

  const targetNodeId = node?.id || PLATFORM_ROOT_ID;
  const isPlatformLevel = targetNodeId === PLATFORM_ROOT_ID;

  const { data: issues = [], isLoading } = useSupportIssues(targetNodeId);
  const { data: raisedIssues = [] } = useRaisedIssues(targetNodeId);
  const createMutation = useCreateIssue(targetNodeId);
  const updateMutation = useUpdateIssueStatus(targetNodeId);
  const escalateMutation = useEscalateIssue(targetNodeId);
  const commentMutation = useAddIssueComment(targetNodeId);

  const { data: deepLinkedTicket } = useIssue(requestedTicketId || '');

  // Support staff see the whole queue; everyone else only sees what they raised.
  const canViewQueue = isGlobalAdmin || can('view_support') || can('SUPPORT_VIEW');
  const canManageTickets = isGlobalAdmin || can('manage_support') || can('SUPPORT_MANAGE');

  const visibleIssues = (queueTab === 'RAISED' ? raisedIssues : issues).filter(i => {
    if (queueTab === 'QUEUE' && !canViewQueue && i.requesterId && user?.id && i.requesterId !== user.id) {
      return false;
    }
    if (filter === 'ALL') return true;
    return i.status === filter;
  });

  // Where a ticket from this node will actually land, matching the backend routing rules:
  // members file locally, anyone with support authority here files upward.
  const submissionTargetLabel = !isPlatformLevel && canViewQueue
    ? node?.lineageIds?.length
      ? `${node.name} → ${parentNodeTerm}`
      : `${node?.name || 'This node'} → Platform`
    : isPlatformLevel
      ? 'Platform Support'
      : node?.name || 'This node';

  const getStatusColor = (status: IssueStatus) => {
    switch (status) {
      case IssueStatus.OPEN: return 'text-blue-500 bg-blue-50 border-blue-100';
      case IssueStatus.INVESTIGATING: return 'text-amber-500 bg-amber-50 border-amber-100';
      case IssueStatus.RESOLVED: return 'text-emerald-500 bg-emerald-50 border-emerald-100';
      case IssueStatus.ESCALATED: return 'text-purple-500 bg-purple-50 border-purple-100';
      case IssueStatus.CLOSED: return 'text-gray-500 bg-white border-black dark:border-white dark:bg-slate-950';
    }
  };

  const getSeverityColor = (severity: IssueSeverity) => {
    switch (severity) {
      case IssueSeverity.LOW: return 'bg-white text-gray-700 dark:bg-slate-800';
      case IssueSeverity.MEDIUM: return 'bg-blue-100 text-blue-700';
      case IssueSeverity.HIGH: return 'bg-orange-100 text-orange-700';
      case IssueSeverity.CRITICAL: return 'bg-red-100 text-red-700 border-2 border-black dark:border-white border-red-200 animate-pulse';
    }
  };

  const selectIssue = (issue: Issue) => {
    setSelectedIssue(issue);
    setResolutionNotes(issue.resolutionNotes || '');
    setReply('');
    setIsConfirmingEscalate(false);
  };

  // Resolve a deep-linked ticket against whatever the loaded views already contain or
  // the direct query (render-phase adjustment instead of an effect).
  const matchIssue = [...issues, ...raisedIssues].find((i) => i.id === requestedTicketId) || deepLinkedTicket || undefined;
  const [prevResolvedTicketId, setPrevResolvedTicketId] = useState<string | null>(null);
  if (requestedTicketId && matchIssue && selectedIssue?.id !== requestedTicketId && prevResolvedTicketId !== requestedTicketId) {
    setPrevResolvedTicketId(requestedTicketId);
    setSelectedIssue(matchIssue);
    setResolutionNotes(matchIssue.resolutionNotes || '');
    setReply('');
    setIsConfirmingEscalate(false);
  }

  const isTerminal = (status: IssueStatus) =>
    status === IssueStatus.RESOLVED || status === IssueStatus.CLOSED;

  const canEscalateSelected = selectedIssue
    ? selectedIssue.nodeId !== PLATFORM_ROOT_ID && !isTerminal(selectedIssue.status)
    : false;

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-none bg-brand-primary/10 flex items-center justify-center text-brand-primary">
            <LifeBuoy size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900 dark:text-white">{supportTerm}</h1>
            <p className="text-gray-500">Submit requests and track resolution progress.</p>
          </div>
        </div>
        {!isCreating && (
          <button
            data-testid="submit-ticket-btn"
            onClick={() => setIsCreating(true)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-none font-bold hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:shadow-brand-primary/20 transition-all"
          >
            <Send size={18} />
            New Ticket
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main List */}
        <div className="lg:col-span-2 space-y-4">
          {canViewQueue && (
            <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
              {(['QUEUE', 'RAISED'] as const).map((tab) => (
                <button
                  key={tab}
                  data-testid={tab === 'QUEUE' ? 'support-admin-queue-tab' : 'support-raised-tab'}
                  onClick={() => { setQueueTab(tab); setSelectedIssue(null); setIsConfirmingEscalate(false); }}
                  className={cn(
                    "px-4 py-2 rounded-none text-xs font-bold whitespace-nowrap border-2 border-black dark:border-white transition-all",
                    queueTab === tab
                      ? "bg-navy-900 text-white border-navy-900 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
                      : "bg-white text-gray-500 dark:text-gray-400 border-black dark:border-white hover:border-brand-primary/30 dark:bg-slate-900"
                  )}
                >
                  {tab === 'QUEUE' ? 'Queue' : `Raised · ${raisedIssues.length}`}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
            {['ALL', ...Object.values(IssueStatus)].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s as any)}
                className={cn(
                  "px-4 py-2 rounded-none text-xs font-bold whitespace-nowrap border-2 border-black dark:border-white transition-all",
                  filter === s
                    ? "bg-navy-900 text-white border-navy-900 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
                    : "bg-white text-gray-500 dark:text-gray-400 border-black dark:border-white hover:border-brand-primary/30 dark:bg-slate-900"
                )}
              >
                {s}
              </button>
            ))}
          </div>

          <div
            data-testid={!canViewQueue ? "my-tickets-section" : undefined}
            className="bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] overflow-hidden dark:bg-slate-900"
          >
            {isLoading ? (
              <div className="p-12 flex justify-center">
                <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-none animate-spin" />
              </div>
            ) : visibleIssues.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-white rounded-none flex items-center justify-center mx-auto mb-4 dark:bg-slate-950">
                  <LifeBuoy className="text-gray-300" size={32} />
                </div>
                <h3 className="text-lg font-bold text-navy-900 dark:text-white">
                  {queueTab === 'RAISED' ? 'Nothing escalated yet' : 'No tickets found'}
                </h3>
                <p className="text-gray-500 max-w-xs mx-auto">
                  {queueTab === 'RAISED'
                    ? 'Tickets you escalate upward stay here so you can keep track of them.'
                    : 'Either you are in the clear, or your filters are too strict.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {visibleIssues.map((issue) => (
                  <motion.div
                    layoutId={issue.id}
                    key={issue.id}
                    data-testid="ticket-row"
                    data-id={issue.id}
                    onClick={() => selectIssue(issue)}
                    className={cn(
                      "p-5 hover:bg-white/80 cursor-pointer transition-colors group dark:bg-slate-950",
                      selectedIssue?.id === issue.id && "bg-brand-primary/[0.02]"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span
                            data-testid="status-badge"
                            data-status={issue.status}
                            className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase border", getStatusColor(issue.status))}
                          >
                            {issue.status}
                          </span>
                          <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase", getSeverityColor(issue.severity))}>
                            {issue.severity}
                          </span>
                        </div>
                        <h3 className="font-bold text-navy-900 dark:text-white group-hover:text-brand-primary transition-colors">{issue.title}</h3>
                        <div className="flex items-center gap-3 text-xs text-gray-400">
                          <div className="flex items-center gap-1">
                            <Clock size={12} />
                            {format(new Date(issue.createdAt), 'MMM d, HH:mm')}
                          </div>
                          <div className="flex items-center gap-1">
                            <User size={12} />
                            {issue.authorName}
                          </div>
                          {issue.addressedToName && (
                            <div className="flex items-center gap-1">
                              <Building2 size={12} />
                              {queueTab === 'RAISED' ? `with ${issue.addressedToName}` : issue.addressedToName}
                            </div>
                          )}
                          {issue.escalatedToSystem && (
                            <div className="flex items-center gap-1 text-purple-500 font-bold">
                              <ShieldAlert size={12} />
                              Platform Escalated
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={20} className="text-gray-300 group-hover:text-brand-primary transition-all group-hover:translate-x-1" />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar: Detail or Form */}
        <div className="space-y-4">
          <AnimatePresence mode="wait">
            {isCreating ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-white p-6 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6 dark:bg-slate-900"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-navy-900 dark:text-white">Submit Ticket</h2>
                  <button onClick={() => setIsCreating(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Title</label>
                    <input
                      data-testid="ticket-title-input"
                      type="text"
                      value={newIssue.title}
                      onChange={(e) => setNewIssue({ ...newIssue, title: e.target.value })}
                      placeholder="Short summary of the issue..."
                      className="w-full px-4 py-3 bg-white border-2 border-black dark:border-white rounded-none focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none transition-all dark:bg-slate-950"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category</label>
                      <select
                        data-testid="ticket-category-select"
                        value={newIssue.category}
                        onChange={(e) => setNewIssue({ ...newIssue, category: e.target.value as IssueCategory })}
                        className="w-full px-4 py-3 bg-white border-2 border-black dark:border-white rounded-none outline-none dark:bg-slate-950"
                      >
                        <option value={IssueCategory.TECHNICAL}>Technical</option>
                        <option value={IssueCategory.COMPLIANCE}>Compliance</option>
                        <option value={IssueCategory.CONDUCT}>Conduct</option>
                        <option value={IssueCategory.BILLING}>Billing</option>
                        <option value={IssueCategory.OTHER}>Other</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Severity</label>
                      <select
                        data-testid="ticket-severity-select"
                        value={newIssue.severity}
                        onChange={(e) => setNewIssue({ ...newIssue, severity: e.target.value as IssueSeverity })}
                        className="w-full px-4 py-3 bg-white border-2 border-black dark:border-white rounded-none outline-none dark:bg-slate-950"
                      >
                        <option value={IssueSeverity.LOW}>Low</option>
                        <option value={IssueSeverity.MEDIUM}>Medium</option>
                        <option value={IssueSeverity.HIGH}>High</option>
                        <option value={IssueSeverity.CRITICAL}>Critical</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Description</label>
                    <textarea
                      data-testid="ticket-description-input"
                      rows={4}
                      value={newIssue.description}
                      onChange={(e) => setNewIssue({ ...newIssue, description: e.target.value })}
                      placeholder="Detailed explanation..."
                      className="w-full px-4 py-3 bg-white border-2 border-black dark:border-white rounded-none focus:ring-2 focus:ring-brand-primary focus:border-transparent outline-none transition-all resize-none dark:bg-slate-950"
                    />
                  </div>

                  <div className="p-3 bg-gray-50 border-2 border-black dark:border-white rounded-none text-xs text-gray-600 flex items-center gap-2 dark:bg-slate-950 dark:text-gray-300">
                    <CornerUpLeft size={14} className="shrink-0" />
                    <span>
                      Goes to <strong className="font-bold">{submissionTargetLabel}</strong>
                    </span>
                  </div>

                  <button
                    data-testid="ticket-submit-btn"
                    disabled={!newIssue.title || !newIssue.description || createMutation.isPending}
                    onClick={() => {
                      createMutation.mutate({
                        ...newIssue,
                        authorName: user?.fullName || user?.username || 'Unknown'
                      });
                      setIsCreating(false);
                      setNewIssue({ title: '', description: '', category: IssueCategory.TECHNICAL, severity: IssueSeverity.MEDIUM });
                    }}
                    className="w-full py-4 bg-brand-primary text-white rounded-none font-bold shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-brand-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale"
                  >
                    {createMutation.isPending ? 'Submitting...' : 'Send Ticket'}
                  </button>
                  <p className="text-[10px] text-gray-400 text-center uppercase tracking-widest leading-relaxed">
                    Tickets move up the hierarchy. If the node you send to cannot resolve it,
                    they escalate it further.
                  </p>
                </div>
              </motion.div>
            ) : selectedIssue ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-white p-6 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] space-y-6 dark:bg-slate-900"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-gray-400">
                    <span className="text-[10px] font-bold uppercase tracking-widest">Ticket Details</span>
                    <button onClick={() => { setSelectedIssue(null); if (requestedTicketId) setSearchParams({}); }}>✕</button>
                  </div>
                  <h2 className="text-xl font-bold text-navy-900 dark:text-white leading-tight">{selectedIssue.title}</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase border", getStatusColor(selectedIssue.status))}>
                      {selectedIssue.status}
                    </span>
                    <span className="text-[10px] text-gray-400 font-mono">#{selectedIssue.id.split('-').pop()}</span>
                  </div>
                </div>

                {/* Where the ticket sits in the hierarchy */}
                <div className="flex items-center gap-2 p-3 bg-gray-50 border-2 border-black dark:border-white text-xs text-gray-600 dark:bg-slate-950 dark:text-gray-300">
                  <Building2 size={14} className="shrink-0" />
                  <span>
                    Addressed to <strong className="font-bold">{selectedIssue.addressedToName || selectedIssue.nodeId}</strong>
                    {selectedIssue.originNodeName && selectedIssue.originNodeId !== selectedIssue.nodeId && (
                      <> · raised at {selectedIssue.originNodeName}</>
                    )}
                  </span>
                </div>

                <div className="p-4 bg-white rounded-none text-sm text-gray-600 dark:text-gray-300 leading-relaxed italic dark:bg-slate-950">
                  "{selectedIssue.description}"
                </div>

                {/* Conversation */}
                {selectedIssue.updates && selectedIssue.updates.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Activity</span>
                    {selectedIssue.updates.map((update, i) => (
                      <div key={i} className="text-xs border-l-2 border-brand-primary/30 pl-3 py-1">
                        <p className="text-gray-700 dark:text-gray-300">{update.message}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {format(new Date(update.timestamp), 'MMM d, HH:mm')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {selectedIssue.escalationTrail && selectedIssue.escalationTrail.length > 0 && (
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Escalation Path</span>
                    {selectedIssue.escalationTrail.map((hop, i) => (
                      <div key={i} className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
                        <ArrowUpRight size={12} />
                        {hop.fromNodeName || hop.fromNodeId} → {hop.toNodeName || hop.toNodeId}
                      </div>
                    ))}
                  </div>
                )}

                {canManageTickets && selectedIssue.status !== IssueStatus.RESOLVED && selectedIssue.status !== IssueStatus.CLOSED && (
                  <div className="space-y-4 pt-4 border-t border-black dark:border-white">
                    <span className="text-[10px] font-bold text-brand-primary uppercase tracking-widest block">Admin Actions</span>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</label>
                      <select
                        data-testid="ticket-status-select"
                        value={selectedIssue.status}
                        onChange={(e) => updateMutation.mutate({
                          id: selectedIssue.id,
                          data: { status: e.target.value as IssueStatus }
                        })}
                        className="w-full px-4 py-3 bg-white border-2 border-black dark:border-white rounded-none outline-none dark:bg-slate-950"
                      >
                        {Object.values(IssueStatus).map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Resolution Notes</label>
                      <textarea
                        data-testid="resolution-notes-input"
                        rows={2}
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        placeholder="How was this resolved?"
                        className="w-full px-4 py-3 bg-white border-2 border-black dark:border-white rounded-none focus:ring-2 focus:ring-brand-primary outline-none resize-none dark:bg-slate-950"
                      />
                      <button
                        data-testid="update-status-btn"
                        disabled={updateMutation.isPending}
                        onClick={() => updateMutation.mutate({
                          id: selectedIssue.id,
                          data: { resolutionNotes }
                        })}
                        className="w-full py-2 bg-blue-50 text-blue-600 rounded-none text-xs font-bold border-2 border-black dark:border-white border-blue-100 hover:bg-blue-100 transition-colors disabled:opacity-50"
                      >
                        {updateMutation.isPending ? 'Saving...' : 'Save Notes'}
                      </button>
                    </div>

                    {canEscalateSelected && (
                      <div className="space-y-2">
                        {!isConfirmingEscalate ? (
                          <button
                            data-testid="escalate-ticket-btn"
                            disabled={escalateMutation.isPending}
                            onClick={() => setIsConfirmingEscalate(true)}
                            className="w-full py-2 bg-purple-50 text-purple-600 rounded-none text-xs font-bold border-2 border-black dark:border-white border-purple-100 hover:bg-purple-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <ArrowUpRight size={14} />
                            {selectedIssue.escalatedToSystem ? 'Escalate to Platform' : 'Escalate Up One Level'}
                          </button>
                        ) : (
                          <div className="p-3 bg-purple-50 border-2 border-purple-200 dark:bg-purple-950/40 space-y-2 rounded-none">
                            <p className="text-xs text-purple-900 dark:text-purple-200 font-bold">
                              Confirm escalation to {selectedIssue.escalatedToSystem ? 'Platform' : 'parent node'}?
                            </p>
                            <div className="flex gap-2">
                              <button
                                data-testid="confirm-escalate-btn"
                                disabled={escalateMutation.isPending}
                                onClick={() => {
                                  escalateMutation.mutate(
                                    { id: selectedIssue.id },
                                    { onSuccess: () => setIsConfirmingEscalate(false) }
                                  );
                                }}
                                className="flex-1 py-1.5 bg-purple-600 text-white rounded-none text-xs font-bold hover:bg-purple-700 transition-colors disabled:opacity-50"
                              >
                                {escalateMutation.isPending ? 'Escalating...' : 'Confirm Escalation'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setIsConfirmingEscalate(false)}
                                className="px-3 py-1.5 bg-white border border-gray-300 dark:bg-slate-900 dark:border-gray-700 rounded-none text-xs font-bold hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => updateMutation.mutate({ id: selectedIssue.id, data: { status: IssueStatus.RESOLVED, resolutionNotes } })}
                      className="w-full py-3 bg-emerald-500 text-white rounded-none text-sm font-bold shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-emerald-500/20 hover:bg-emerald-600 transition-all flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 size={18} />
                      Mark Resolved
                    </button>
                  </div>
                )}

                {selectedIssue.status === IssueStatus.RESOLVED && (
                  <div className="p-4 bg-emerald-50 border-2 border-black dark:border-white border-emerald-100 rounded-none text-emerald-700 text-xs font-medium flex items-start gap-3">
                    <CheckCircle2 size={16} className="shrink-0" />
                    <div>
                      This issue was resolved by the administration.
                      {selectedIssue.resolutionNotes && (
                        <p className="mt-2 text-[10px] font-normal italic opacity-80 underline underline-offset-2">
                          Note: {selectedIssue.resolutionNotes}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Reply */}
                <div className="pt-4 border-t border-black dark:border-white space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Add Reply</label>
                  <textarea
                    data-testid="ticket-reply-input"
                    rows={2}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Write an update..."
                    className="w-full px-4 py-3 bg-white border-2 border-black dark:border-white rounded-none focus:ring-2 focus:ring-brand-primary outline-none resize-none dark:bg-slate-950"
                  />
                  <button
                    data-testid="ticket-reply-submit"
                    disabled={!reply.trim() || commentMutation.isPending}
                    onClick={() => {
                      commentMutation.mutate({ id: selectedIssue.id, message: reply });
                      setReply('');
                    }}
                    className="w-full py-2 bg-brand-primary text-white rounded-none text-xs font-bold hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:grayscale"
                  >
                    <SendHorizontal size={14} />
                    {commentMutation.isPending ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>

                <div className="pt-4 border-t border-black dark:border-white space-y-3">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <div className="w-8 h-8 rounded-none bg-white flex items-center justify-center font-bold text-gray-400 dark:bg-slate-800">
                      {selectedIssue.authorName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-navy-900 dark:text-white">{selectedIssue.authorName}</p>
                      <p className="text-[10px]">
                        Raised at {selectedIssue.originNodeName || selectedIssue.originNodeId || 'Platform'}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-brand-primary/5 p-8 rounded-none border-2 border-black dark:border-white border-brand-primary/10 text-center space-y-4"
              >
                <div className="w-16 h-16 bg-white rounded-none flex items-center justify-center mx-auto shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
                  <MessageSquare className="text-brand-primary" size={24} />
                </div>
                <h3 className="text-lg font-bold text-brand-primary">Need Clarification?</h3>
                <p className="text-gray-500 text-sm">
                  Check our documentation or reach out to other members in the discussion board first.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};